import type {
  DynamicConfig,
  StatsigOptions,
  StatsigUser,
} from '@statsig/js-client';
import { StatsigClient } from '@statsig/js-client';

import { getAffiliateCookieData, isStena } from '@libs/cookie';
import { getDomain, log } from '@libs/utils';

import {
  FeaturePrefix,
  getActiveFeatureToggles,
} from '../customFeatureToggles';

import { migrateOldSdkStableIdToNewSdk } from './compatibility';
import type {
  StatsigExperimentName,
  StatsigMultiExperimentName,
} from './statsigExperiments';

type CustomerType =
  | 'aiTrip'
  | 'publishedTrip'
  | 'meta'
  | 'direct'
  | 'bookingPage'
  | 'carTrip'
  | 'marketAutoComplete'
  | 'marketFlightOnly';

interface StatsigUserMetadata {
  customerType: CustomerType;
  isStena: boolean;
  userSource?: string;
}

const STATSIG_ENABLED = true;
let statsigClient: StatsigClient;

const urlParams = new URL(window.location.href).searchParams;

export async function initializeStatsig(): Promise<void> {
  if (STATSIG_ENABLED) {
    lazyLoadStatsig();

    await statsigClient.initializeAsync();

    log({ title: 'Statsig', context: ['Initialized'] });
  }
}

export function checkGate(gateName: string): boolean {
  const gate = statsigClient.checkGate(gateName);

  log({ title: 'Statsig', context: ['Gate', { gateName, gate }] });

  return gate;
}

export function getExperiment(
  experimentName: StatsigExperimentName | StatsigMultiExperimentName
): DynamicConfig {
  const experiment = statsigClient.getExperiment(experimentName);

  log({
    title: 'Statsig',
    context: ['Experiment', { experimentName, experiment }],
  });

  return experiment;
}

export function logStatsigEvent(
  eventName: string,
  eventValue?: string | number,
  eventData?: Record<string, string>
): void {
  log({
    title: 'Statsig',
    context: ['Event', { eventName, eventValue, eventData }],
  });
  statsigClient?.logEvent(eventName, eventValue, eventData);
}

function lazyLoadStatsig(): void {
  if (STATSIG_ENABLED) {
    if (statsigClient) {
      return;
    }

    statsigClient = createStatsigClient();
  }
}

function createStatsigClient(): StatsigClient {
  migrateOldSdkStableIdToNewSdk();

  const sdkKey = getDomain.isProduction
    ? import.meta.env.VITE_STATSIG_CLIENT_PROD
    : import.meta.env.VITE_STATSIG_CLIENT_DEV;

  const user: StatsigUser = {
    custom: {
      'btf-tp': [
        ...Object.keys(getActiveFeatureToggles(FeaturePrefix.TripPlanner)),
      ],
      ...getUserMetadata(),
    },
    locale: getDomain.getCurrentDomainSpec().Locale,
  };

  const options: StatsigOptions = {
    environment: {
      tier: getDomain.isProduction ? 'production' : 'development',
    },
  };

  log({ title: 'Statsig', context: ['Loaded', { user, options }] });

  return new StatsigClient(sdkKey, user, options);
}

function getUserMetadata(): StatsigUserMetadata {
  const customerType = ((): CustomerType => {
    if (urlParams.has('ai-trip-id')) {
      return 'aiTrip';
    }
    if (urlParams.has('published-trip-id')) {
      return 'publishedTrip';
    }
    if (urlParams.has('affiliate-id')) {
      return 'meta';
    }
    if (
      urlParams.has('customer-origin') &&
      urlParams.get('customer-origin') === 'booking-page'
    ) {
      return 'bookingPage';
    }
    if (window.location.href.includes('car-trip')) {
      return 'carTrip';
    }
    if (
      urlParams.has('customer-origin') &&
      urlParams.get('customer-origin') === 'marketplace-hotel-search'
    ) {
      return 'marketAutoComplete';
    }
    if (
      urlParams.has('customer-origin') &&
      urlParams.get('customer-origin') === 'marketplace-flight-search'
    ) {
      return 'marketFlightOnly';
    }
    return 'direct';
  })();

  const userSource = getAffiliateCookieData()?.affiliateCookieParams.utm_medium;

  return {
    customerType,
    isStena: isStena(),
    ...(userSource && { userSource }),
  };
}
